import { Box, Popover, Typography } from "@mui/material";
import { FC, ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { logoutAction } from "../../../redux/actions/authAction";
import { MasterSliceActions } from "../../../redux/slice/masterSlice";
import ConfirmDialog from "../../atoms/confirm-box/confirm-box";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { NotificationDrawerContent } from "./notification-drawe-content";
import { NavSvgIcon } from "../admin-side-nav/admin-side-nav";
import { findAdminNavItem } from "../admin-side-nav/admin-nav-items";
import { Colors } from "../../../configs/colors";
import {
  IMerchantResponse,
  IStoreResponse,
} from "../../../typings/interfaces/responses/all-merchants-response";
import { primaryAlpha } from "../../../configs/colors";

const BORDER = "#E5E7EB";
const GREEN_SOFT = "#EAF8F1";

// Square header button (bell, menu)
const iconButtonSx = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "3.6rem",
  height: "3.6rem",
  flexShrink: 0,
  borderRadius: "0.85rem",
  border: `1px solid ${BORDER}`,
  backgroundColor: "#ffffff",
  color: "#374151",
  cursor: "pointer",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  "&:hover": { borderColor: "#D1D5DB" },
  "&:focus-visible": { outline: `2px solid ${Colors.PRIMARY}`, outlineOffset: "1px" },
} as const;

const dropdownPaperSx = {
  mt: "0.6rem",
  borderRadius: "1.35rem",
  border: `1px solid ${BORDER}`,
  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  overflow: "hidden",
} as const;

const DropdownHeader: FC<{ title: string; children?: ReactNode }> = ({ title, children }) => (
  <Box sx={{ p: "1rem 1.35rem", borderBottom: "1px solid #F3F4F6", backgroundColor: "#F9FAFB" }}>
    <Typography
      sx={{
        fontSize: "var(--fs-2xs)",
        fontWeight: 700,
        letterSpacing: "0.12em",
        color: "#6B7280",
        textTransform: "uppercase",
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

// "Presto Valet - Store" → "PV"
const storeCode = (name?: string) =>
  (name ?? "")
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("") || "—";

const StoreCodeChip: FC<{ name?: string; size?: string }> = ({ name, size = "2.4rem" }) => (
  <Box
    component="span"
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      borderRadius: "0.7rem",
      backgroundColor: GREEN_SOFT,
      color: Colors.PRIMARY,
      fontSize: "var(--fs-2xs)",
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    {storeCode(name)}
  </Box>
);

const StoreSwitcher: FC = () => {
  const dispatch = useAppDispatch();
  const { adminDashboardSelectedStore } = useAppSelector((state) => state.master);
  const { merchantListResponse } = useAppSelector((state) => state.merchant);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const merchants: IMerchantResponse[] = merchantListResponse?.body ?? [];

  const select = (store: IStoreResponse, merchant: IMerchantResponse) => {
    dispatch(MasterSliceActions.setAdminDashboardSelectedStore(store));
    dispatch(MasterSliceActions.setAdminDashboardSelectedMerchant(merchant));
    setAnchor(null);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={!!anchor}
        aria-label="Switch store location"
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.85rem",
          height: "3.6rem",
          minWidth: { xs: 0, md: "19.5rem" },
          maxWidth: "26rem",
          p: { xs: "0 0.6rem", md: "0 1.2rem" },
          borderRadius: "0.85rem",
          border: `1px solid ${anchor ? Colors.PRIMARY : BORDER}`,
          backgroundColor: "#ffffff",
          cursor: "pointer",
          fontFamily: "inherit",
          boxShadow: anchor ? `0 0 0 3px ${primaryAlpha(0.10)}` : "0 1px 3px rgba(0,0,0,0.06)",
          "&:focus-visible": { outline: `2px solid ${Colors.PRIMARY}`, outlineOffset: "1px" },
        }}
      >
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "0.85rem", minWidth: 0 }}>
          <StoreCodeChip name={adminDashboardSelectedStore?.storeName} />
          <Box
            component="span"
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: "var(--fs-xs)",
                fontWeight: 600,
                color: "#111827",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {adminDashboardSelectedStore?.storeName ?? "Select Store"}
            </Box>
            <Box component="span" sx={{ fontSize: "var(--fs-2xs)", fontWeight: 500, color: "#6B7280" }}>
              Switch Store Location
            </Box>
          </Box>
        </Box>
        <Box
          component="span"
          sx={{
            display: "flex",
            color: "#9CA3AF",
            transform: anchor ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        >
          <NavSvgIcon size="1.05rem">
            <path d="M6 9l6 6 6-6" />
          </NavSvgIcon>
        </Box>
      </Box>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { ...dropdownPaperSx, width: "min(25rem, calc(100vw - 2rem))" } } }}
      >
        <DropdownHeader title="Select Store Location" />
        <Box role="listbox" aria-label="Store locations" sx={{ p: "0.6rem", maxHeight: "60vh", overflowY: "auto" }}>
          {merchants.length === 0 && (
            <Typography sx={{ fontSize: "var(--fs-xs)", color: "#6B7280", p: "0.85rem" }}>
              No stores found
            </Typography>
          )}
          {merchants.flatMap((merchant) =>
            merchant.stores.map((store) => {
              const selected = adminDashboardSelectedStore?.storeId === store.storeId;
              return (
                <Box
                  key={`${merchant.merchantId}-${store.storeId}`}
                  component="button"
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => select(store, merchant)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.85rem",
                    width: "100%",
                    p: "0.85rem 1rem",
                    mb: "0.35rem",
                    border: "none",
                    borderRadius: "0.85rem",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "inherit",
                    backgroundColor: selected ? GREEN_SOFT : "transparent",
                    "&:hover": { backgroundColor: selected ? GREEN_SOFT : "#F4FBF7" },
                  }}
                >
                  <StoreCodeChip name={store.storeName} size="3.2rem" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "var(--fs-xs)",
                        fontWeight: 700,
                        color: selected ? Colors.PRIMARY : "#111827",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {store.storeName}
                    </Typography>
                    <Typography sx={{ fontSize: "var(--fs-2xs)", color: "#6B7280" }}>
                      {merchant.companyName}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

const UserMenu: FC<{ onLogout(): void }> = ({ onLogout }) => {
  const { user } = useAppSelector((state) => state.auth);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-label="User actions"
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        sx={{
          ...iconButtonSx,
          borderRadius: "50%",
          border: "1px solid #D1D5DB",
          background: "linear-gradient(135deg,#D1D5DB,#9CA3AF)",
          color: "#ffffff",
        }}
      >
        <NavSvgIcon size="1.55rem">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </NavSvgIcon>
      </Box>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { ...dropdownPaperSx, width: "17rem" } } }}
      >
        <DropdownHeader title="User Actions">
          {(name || user?.email) && (
            <Box sx={{ mt: "0.35rem", minWidth: 0 }}>
              {name && (
                <Typography sx={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: "#111827" }}>{name}</Typography>
              )}
              {user?.email && (
                <Typography
                  sx={{
                    fontSize: "var(--fs-2xs)",
                    color: "#6B7280",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.email}
                </Typography>
              )}
            </Box>
          )}
        </DropdownHeader>
        <Box role="menu" sx={{ p: "0.6rem" }}>
          <Box
            component="button"
            type="button"
            role="menuitem"
            onClick={() => {
              setAnchor(null);
              onLogout();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.85rem",
              width: "100%",
              p: "0.85rem 1rem",
              border: "none",
              borderRadius: "0.85rem",
              cursor: "pointer",
              backgroundColor: "transparent",
              fontFamily: "inherit",
              fontSize: "var(--fs-xs)",
              fontWeight: 600,
              color: "#DC2626",
              textAlign: "left",
              "&:hover": { backgroundColor: "#FEF2F2" },
            }}
          >
            <NavSvgIcon size="1.2rem" color="#DC2626">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </NavSvgIcon>
            Log Out
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export interface IAdminTopNavProps {
  /** Shows the menu button (small screens) */
  showMenuButton?: boolean;
  onMenuClick?(): void;
}

export const AdminTopNav: FC<IAdminTopNavProps> = ({ showMenuButton, onMenuClick }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const title = findAdminNavItem(location.pathname).item?.title ?? "Admin";

  return (
    <Box
      component="header"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1.2rem",
        flexShrink: 0,
        minHeight: { xs: "6.5rem", md: "9rem" },
        p: { xs: "1rem 1.2rem", md: "0 2.4rem" },
        borderBottom: `1px solid ${BORDER}`,
        backgroundColor: "#FBFBFB",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", minWidth: 0 }}>
        {showMenuButton && (
          <Box component="button" type="button" aria-label="Open menu" onClick={onMenuClick} sx={iconButtonSx}>
            <NavSvgIcon size="1.55rem">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </NavSvgIcon>
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              display: { xs: "none", sm: "block" },
              fontSize: "var(--fs-2xs)",
              fontWeight: 600,
              color: "#9CA3AF",
            }}
          >
            Presto POS Admin
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "var(--fs-lg)", md: "var(--fs-2xl)" },
              fontWeight: 800,
              color: "#374151",
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              mt: { xs: 0, sm: "0.3rem" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "0.85rem", flexShrink: 0 }}>
        <StoreSwitcher />
        <Box
          component="button"
          type="button"
          aria-label="Notifications"
          onClick={() => setNotificationsOpen(true)}
          sx={iconButtonSx}
        >
          <NavSvgIcon size="1.55rem">
            <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 0 0-4-5.7V4a2 2 0 0 0-4 0v1.3A6 6 0 0 0 6 11v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
            <path d="M9 17a3 3 0 0 0 6 0" />
          </NavSvgIcon>
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: "0.75rem",
              right: "0.75rem",
              width: "0.7rem",
              height: "0.7rem",
              borderRadius: "50%",
              backgroundColor: "#EF4444",
              border: "2px solid #ffffff",
            }}
          />
        </Box>
        <UserMenu onLogout={() => setConfirmOpen(true)} />
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Log out"
        message="Are you sure you want to log out?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          dispatch(logoutAction());
        }}
      />

      <CustomDrawer isOpen={notificationsOpen} toggleDrawer={() => setNotificationsOpen((open) => !open)}>
        <NotificationDrawerContent />
      </CustomDrawer>
    </Box>
  );
};
